import { SecureLoader, ThemeProvider } from "@ews-admin/global-design-system";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Provider, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { AnalyticsPageViews } from "./analytics/AnalyticsPageViews";
import { LegalRedirect } from "./components/common/LegalRedirect";
import { PageLoadBoundary } from "./components/common/PageLoadBoundary";
import { PrivateRoute } from "./components/common/PrivateRoute";
import { RedirectByRole } from "./components/common/RedirectByRole";
import { SessionEndedNotice } from "./components/common/SessionEndedNotice";
import { SessionProvider } from "./components/common/SessionProvider";
import { LicencePaymentDueBanner } from "./components/molecules/LicencePaymentDueBanner/LicencePaymentDueBanner";
import { PasswordChangeRequiredModal } from "./components/molecules/PasswordChangeRequiredModal/PasswordChangeRequiredModal";
import { SlowConnectionBanner } from "./components/molecules/SlowConnectionBanner/SlowConnectionBanner";
import { CookieConsent } from "./components/organisms/CookieConsent/CookieConsent";
import ErrorModal from "./components/organisms/ErrorModal/ErrorModal";
import { useAppNotices } from "./hooks/useAppNotices";
import { useFeatureFlag } from "./hooks/useFeatureFlags";
import { ComingSoon } from "./pages/ComingSoon";
import { MaintenancePage } from "./pages/maintenance/MaintenancePage";
import { selectMaintenanceFlag } from "./store/reducers/appNoticeSlice";
import { selectLanguage } from "./store/reducers/sessionSlice";
import { RootState, store } from "./store/store";
import { SessionStatus } from "./types/session.definition";
import { lazyPage } from "./utils/lazy-page";
import { maintenanceRedirectPath } from "./utils/maintenance-utils";

// MED-1743: each route downloads its own code. The maintenance and coming-soon
// pages stay in the main bundle: they must show even when everything else fails.
const EWSDesignSystemExample = lazyPage(
  () => import("./components/examples/EWSDesignSystemExample"),
  "EWSDesignSystemExample"
);
const ChangePassword = lazyPage(
  () => import("./pages/ChangePassword"),
  "ChangePassword"
);
const DeveloperPage = lazyPage(
  () => import("./pages/developer/DeveloperPage"),
  "DeveloperPage"
);
const AppointmentDetails = lazyPage(
  () => import("./pages/doctor/AppointmentDetails"),
  "AppointmentDetails"
);
const DoctorLicenceInvoicePage = lazyPage(
  () => import("./pages/doctor/DoctorLicenceInvoicePage"),
  "DoctorLicenceInvoicePage"
);
const DoctorNotifications = lazyPage(
  () => import("./pages/doctor/DoctorNotifications"),
  "DoctorNotifications"
);
const LicencePage = lazyPage(
  () => import("./pages/doctor/LicencePage"),
  "LicencePage"
);
const ManageAppointments = lazyPage(
  () => import("./pages/doctor/ManageAppointments"),
  "ManageAppointments"
);
const ManageSchedule = lazyPage(
  () => import("./pages/doctor/ManageSchedule"),
  "ManageSchedule"
);
const PatientDetails = lazyPage(
  () => import("./pages/doctor/PatientDetails"),
  "PatientDetails"
);
const PatientMedicalHistory = lazyPage(
  () => import("./pages/doctor/PatientMedicalHistory"),
  "PatientMedicalHistory"
);
const ViewPatients = lazyPage(
  () => import("./pages/doctor/ViewPatients"),
  "ViewPatients"
);
const WritePrescription = lazyPage(
  () => import("./pages/doctor/WritePrescription"),
  "WritePrescription"
);
const DoctorDetails = lazyPage(
  () => import("./pages/DoctorDetails"),
  "DoctorDetails"
);
const DoctorProfile = lazyPage(
  () => import("./pages/DoctorProfile"),
  "DoctorProfile"
);
const DoctorsSearch = lazyPage(
  () => import("./pages/DoctorsSearch"),
  "default"
);
const EditDoctorProfile = lazyPage(
  () => import("./pages/EditDoctorProfile"),
  "EditDoctorProfile"
);
const EditPatientProfile = lazyPage(
  () => import("./pages/EditPatientProfile"),
  "EditPatientProfile"
);
const FacilityDetails = lazyPage(
  () => import("./pages/FacilityDetails"),
  "FacilityDetails"
);
const FacilitySearch = lazyPage(
  () => import("./pages/FacilitySearch"),
  "FacilitySearch"
);
const Home = lazyPage(() => import("./pages/Home"), "Home");
const ManagerProfile = lazyPage(
  () => import("./pages/ManagerProfile"),
  "ManagerProfile"
);
const NotFound = lazyPage(() => import("./pages/NotFound"), "default");
const Partners = lazyPage(() => import("./pages/Partners"), "Partners");
const PatientAppointments = lazyPage(
  () => import("./pages/PatientAppointments"),
  "PatientAppointments"
);
const PatientProfile = lazyPage(
  () => import("./pages/PatientProfile"),
  "PatientProfile"
);
const PaymentCallbackPage = lazyPage(
  () => import("./pages/payment/PaymentCallbackPage"),
  "PaymentCallbackPage"
);
const PaymentCancelledPage = lazyPage(
  () => import("./pages/payment/PaymentCancelledPage"),
  "PaymentCancelledPage"
);
const Register = lazyPage(() => import("./pages/Register"), "default");
const HealthcareProfessionalServices = lazyPage(
  () => import("./pages/services/HealthcareProfessionalServices"),
  "HealthcareProfessionalServices"
);
const MedicalFacilityServices = lazyPage(
  () => import("./pages/services/MedicalFacilityServices"),
  "MedicalFacilityServices"
);
const PatientServices = lazyPage(
  () => import("./pages/services/PatientServices"),
  "PatientServices"
);
const OnlineChat = lazyPage(
  () => import("./pages/services/virtual/OnlineChat"),
  "OnlineChat"
);
const RemoteMonitoring = lazyPage(
  () => import("./pages/services/virtual/RemoteMonitoring"),
  "RemoteMonitoring"
);
const VideoConsultation = lazyPage(
  () => import("./pages/services/virtual/VideoConsultation"),
  "VideoConsultation"
);
const VirtualAssessment = lazyPage(
  () => import("./pages/services/virtual/VirtualAssessment"),
  "VirtualAssessment"
);
const DoctorProfileSettings = lazyPage(
  () => import("./pages/settings/DoctorProfileSettings"),
  "DoctorProfileSettings"
);
const ManagerSettings = lazyPage(
  () => import("./pages/settings/ManagerProfileSettings"),
  "ManagerSettings"
);
const PatientProfileSettings = lazyPage(
  () => import("./pages/settings/PatientProfileSettings"),
  "PatientProfileSettings"
);

const AppRoutes = () => {
  const { t } = useTranslation();
  const comingSoonEnabled = import.meta.env.VITE_COMING_SOON === "true";
  const { sessionStatus, session } = useSelector(
    (state: RootState) => state.session
  );
  const maintenance = useSelector(selectMaintenanceFlag);
  const language = useSelector(selectLanguage);
  const isDoctorLicenceEnabled = useFeatureFlag("DOCTOR_LICENCE");
  const location = useLocation();

  // Load MAINTENANCE / ANNOUNCEMENT app notices on first render and on
  // country changes (fail-open).
  useAppNotices();

  // Check if password change is required (don't show on change-password page)
  const requiresPasswordChange =
    session?.user?.requiresPasswordChange === true &&
    location.pathname !== "/change-password";

  if (
    comingSoonEnabled &&
    (!sessionStatus || sessionStatus === SessionStatus.INVALID_SESSION)
  ) {
    return <ComingSoon />;
  }

  // Maintenance gate: once flags are loaded, force every route except
  // `/maintenance` to redirect to the maintenance page. We only enforce after
  // `loaded === true` to avoid a flash of the wrong content on first render.
  // The resolved language rides along on the redirect (see
  // `maintenanceRedirectPath`) so a language-prefixed visit such as `/ar` does
  // not land on a French maintenance page.
  const isOnMaintenancePage = location.pathname === "/maintenance";
  const shouldForceMaintenance =
    maintenance.loaded && maintenance.enabled && !isOnMaintenancePage;

  return (
    <>
      <RedirectByRole />
      <ToastContainer />
      <LicencePaymentDueBanner />
      <PasswordChangeRequiredModal isOpen={requiresPasswordChange} />
      {shouldForceMaintenance && (
        <Navigate to={maintenanceRedirectPath(language)} replace />
      )}
      <PageLoadBoundary key={location.pathname}>
        <Suspense fallback={<SecureLoader label={t("common.loading")} />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/register/patient" element={<Register />} />
            <Route path="/doctors" element={<DoctorsSearch />} />
            <Route path="/doctors/search" element={<DoctorsSearch />} />

            {/* Service Pages */}
            <Route path="/services/patients" element={<PatientServices />} />
            <Route
              path="/services/healthcare-professionals"
              element={<HealthcareProfessionalServices />}
            />
            <Route
              path="/services/medical-facilities"
              element={<MedicalFacilityServices />}
            />
            <Route
              path="/services/video-consultations"
              element={<VideoConsultation />}
            />
            <Route path="/services/online-chat" element={<OnlineChat />} />
            <Route
              path="/services/virtual-assessment"
              element={<VirtualAssessment />}
            />
            <Route
              path="/services/remote-monitoring"
              element={<RemoteMonitoring />}
            />

            {/* Protected Routes */}
            <Route
              path="/profile"
              element={
                <PrivateRoute allowedRoles={["patient"]}>
                  <PatientProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <PrivateRoute allowedRoles={["patient"]}>
                  <PatientAppointments />
                </PrivateRoute>
              }
            />

            <Route
              path="/appointments/:appointmentId"
              element={
                <PrivateRoute allowedRoles={["patient"]}>
                  <AppointmentDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile/settings"
              element={
                <PrivateRoute allowedRoles={["patient"]}>
                  <PatientProfileSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/profile"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <DoctorProfile />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/settings"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <DoctorProfileSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/profile/edit"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <EditDoctorProfile />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile/edit"
              element={
                <PrivateRoute allowedRoles={["patient"]}>
                  <EditPatientProfile />
                </PrivateRoute>
              }
            />

            <Route path="/doctors/:doctorId" element={<DoctorDetails />} />
            <Route
              path="/doctor/appointments"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <ManageAppointments />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/appointments/:appointmentId"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <AppointmentDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/notifications"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <DoctorNotifications />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/schedule"
              element={
                <PrivateRoute allowedRoles={["doctor"]} requiresLicence>
                  <ManageSchedule />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/:doctorId/patients"
              element={
                <PrivateRoute allowedRoles={["doctor"]} requiresLicence>
                  <ViewPatients />
                </PrivateRoute>
              }
            />
            <Route
              path="/doctor/:doctorId/patients/:patientId"
              element={
                <PrivateRoute allowedRoles={["doctor"]} requiresLicence>
                  <PatientDetails />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/patients/:patientId/history"
              element={
                <PrivateRoute allowedRoles={["doctor"]} requiresLicence>
                  <PatientMedicalHistory />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/prescriptions/new"
              element={
                <PrivateRoute allowedRoles={["doctor"]} requiresLicence>
                  <WritePrescription />
                </PrivateRoute>
              }
            />

            <Route
              path="/doctor/licence"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  {isDoctorLicenceEnabled ? (
                    <LicencePage />
                  ) : (
                    <Navigate to="/" />
                  )}
                </PrivateRoute>
              }
            />

            {/* MED-1686: printable licence invoice (also linked from the receipt email) */}
            <Route
              path="/doctor/licence/invoices/:invoiceId"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  {isDoctorLicenceEnabled ? (
                    <DoctorLicenceInvoicePage />
                  ) : (
                    <Navigate to="/" />
                  )}
                </PrivateRoute>
              }
            />

            <Route path="/facilities" element={<FacilitySearch />} />
            <Route
              path="/facilities/:facilityId"
              element={<FacilityDetails />}
            />
            <Route path="/partners" element={<Partners />} />
            <Route path="/terms" element={<LegalRedirect document="terms" />} />
            <Route
              path="/privacy"
              element={<LegalRedirect document="privacy" />}
            />

            {/* EWS Design System Example - Development only */}
            <Route path="/design-system" element={<EWSDesignSystemExample />} />

            {/* Developer Integration page */}
            <Route path="/developer" element={<DeveloperPage />} />

            {/* Manager Profile Route */}
            <Route
              path="/manager/profile"
              element={
                <PrivateRoute allowedRoles={["manager"]}>
                  <ManagerProfile />
                </PrivateRoute>
              }
            />

            <Route
              path="/manager/settings"
              element={
                <PrivateRoute allowedRoles={["manager"]}>
                  <ManagerSettings />
                </PrivateRoute>
              }
            />

            {/* Change Password Route */}
            <Route
              path="/change-password"
              element={
                <PrivateRoute allowedRoles={["patient", "doctor"]}>
                  <ChangePassword />
                </PrivateRoute>
              }
            />

            {/* Payment pages — no nav header, doctor only */}
            <Route
              path="/payment/callback"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <PaymentCallbackPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/payment/cancelled"
              element={
                <PrivateRoute allowedRoles={["doctor"]}>
                  <PaymentCancelledPage />
                </PrivateRoute>
              }
            />

            {/* Maintenance route — fully isolated, no nav header, no auth */}
            <Route path="/maintenance" element={<MaintenancePage />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PageLoadBoundary>
      <AnalyticsPageViews />
      <CookieConsent />
      <ErrorModal />
      <SlowConnectionBanner />
      <SessionEndedNotice />
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <SessionProvider>
          <ThemeProvider defaultTheme="PROMED">
            <AppRoutes />
          </ThemeProvider>
        </SessionProvider>
      </Router>
    </Provider>
  );
}

export default App;
